import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { ShiftHandoverIndexResponse } from "./types/shiftHandover/responses/ShiftHandoverIndexResponse";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { CreateShiftHandoverRequest } from "./types/shiftHandover/requests/CreateShiftHandoverRequest";
import { SubmitShiftHandoverRequest } from "./types/shiftHandover/requests/SubmitShiftHandoverRequest";
import { AddUrgentConcernRequest } from "./types/shiftHandover/requests/AddUrgentConcernRequest";
import { AddUrgentConcernCommentRequest } from "./types/shiftHandover/requests/AddUrgentConcernCommentRequest";
import { DeleteUrgentConcernRequest } from "./types/shiftHandover/requests/DeleteUrgentConcernRequest";
import { UpdateUrgentConcernStatusRequest } from "./types/shiftHandover/requests/UpdateUrgentConcernStatusRequest";
import { IndexUrgentConcernsRequest } from "./types/shiftHandover/requests/IndexUrgentConcernsRequest";
import { GetHandoverIndexRequest } from "./types/shiftHandover/requests/GetHandoverIndexRequest";
import { ResidentShiftHandoverResponse } from "./types/shiftHandover/responses/ResidentShiftHandoverResponse";
import { ShiftHandoverIndexModel } from "./types/shiftHandover/models/ShiftHandoverIndexModel";
import { ShiftHandoverDashboardResponse } from "./types/shiftHandover/responses/ShiftHandoverDashboardResponse";
import { ShiftHandoverDashboardRequest } from "./types/shiftHandover/requests/ShiftHandoverDashboardRequest";
import { DeleteShiftHandoverRequest } from "./types/shiftHandover/requests/DeleteShiftHandoverRequest";
import { UrgentConcernModel } from "./types/shiftHandover/models/UrgentConcernModel";

export const shiftHandoverApi = createApi({
  reducerPath: "shiftHandoverApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["ShiftHandover", "ShiftConcerns", "ShiftHandoverDashboard"],
  endpoints: (builder) => ({
    getShiftHandoverIndex: builder.query<
      ShiftHandoverIndexResponse,
      GetHandoverIndexRequest
    >({
      query: (params) => ({
        url: "/shift-handovers",
        method: "GET",
        params,
      }),
      providesTags: ["ShiftHandover"],
    }),
    getResidentShiftHandover: builder.query<
      ResidentShiftHandoverResponse,
      number
    >({
      query: (residentId) => ({
        url: `/residents/${residentId}/shift-handover`,
        method: "GET",
      }),
      providesTags: ["ShiftHandover"],
    }),
    getUrgentConcern: builder.query<UrgentConcernModel, number>({
      query: (concernId) => ({
        url: `/shift-handovers/urgent-concerns/${concernId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, concernId) => [
        { type: "ShiftConcerns", id: concernId },
      ],
    }),

    createShiftHandovers: builder.mutation<
      ShiftHandoverIndexModel,
      CreateShiftHandoverRequest
    >({
      query: (body) => ({
        url: "/shift-handovers",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ShiftHandover"],
    }),
    submitShiftHandovers: builder.mutation<
      SuccessResponse,
      SubmitShiftHandoverRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/${payload.params.shiftHandoverId}/submit`,
        method: "POST",
        body: payload.body,
      }),
      invalidatesTags: ["ShiftHandover"],
    }),
    deleteShiftHandover: builder.mutation<
      SuccessResponse,
      DeleteShiftHandoverRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/${payload.shiftHandoverId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ShiftHandover"],
    }),
    addUrgentConcern: builder.mutation<
      SuccessResponse,
      AddUrgentConcernRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/urgent-concerns`,
        method: "POST",
        body: payload.body,
      }),
      invalidatesTags: [
        "ShiftConcerns",
        "ShiftHandover",
        "ShiftHandoverDashboard",
      ],
    }),
    updateUrgentConcernStatus: builder.mutation<
      SuccessResponse,
      UpdateUrgentConcernStatusRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/urgent-concerns/${payload.params.id}`,
        method: "PUT",
        body: payload.body,
      }),
      invalidatesTags: [
        "ShiftConcerns",
        "ShiftHandover",
        "ShiftHandoverDashboard",
      ],
    }),
    deleteUrgentConcern: builder.mutation<
      SuccessResponse,
      DeleteUrgentConcernRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/urgent-concerns/${payload.urgentConcernId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ShiftConcerns",
        "ShiftHandover",
        "ShiftHandoverDashboard",
      ],
    }),
    addUrgentConcernComment: builder.mutation<
      SuccessResponse,
      AddUrgentConcernCommentRequest
    >({
      query: (payload) => ({
        url: `/shift-handovers/urgent-concerns/${payload.params.concernId}/comments`,
        method: "POST",
        body: payload.body,
      }),
      invalidatesTags: [
        "ShiftConcerns",
        "ShiftHandover",
        "ShiftHandoverDashboard",
      ],
    }),
    indexUrgentConcerns: builder.query<
      SuccessResponse,
      IndexUrgentConcernsRequest
    >({
      query: (params) => ({
        url: `/shift-handover/urgent-concerns`,
        method: "GET",
        params,
      }),
      providesTags: ["ShiftConcerns"],
    }),
    getShiftHandoverDashboard: builder.query<
      ShiftHandoverDashboardResponse,
      ShiftHandoverDashboardRequest | void
    >({
      query: (params) => ({
        url: "/shift-handovers/dashboard",
        method: "GET",
        params: params ?? {},
      }),
      providesTags: ["ShiftHandoverDashboard"],
    }),
  }),
});

export const {
  useGetShiftHandoverIndexQuery,
  useGetResidentShiftHandoverQuery,
  useGetUrgentConcernQuery,
  useCreateShiftHandoversMutation,
  useSubmitShiftHandoversMutation,
  useDeleteShiftHandoverMutation,
  useAddUrgentConcernMutation,
  useAddUrgentConcernCommentMutation,
  useUpdateUrgentConcernStatusMutation,
  useDeleteUrgentConcernMutation,
  useIndexUrgentConcernsQuery,
  useGetShiftHandoverDashboardQuery,
} = shiftHandoverApi;
